import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ExternalLink, GraduationCap, HeartHandshake, Video } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { PATHWAYS } from "@/data/pathways";

export default function BookingPage({ pathwayKey }) {
  const params = useParams();
  const key = pathwayKey || params.pathway;
  const pathway = PATHWAYS[key] && key !== "complete" ? PATHWAYS[key] : PATHWAYS.legacy;
  const { review } = pathway;
  const Icon = pathway.icon;
  const [url, setUrl] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    setUrl("");
    setLoaded(false);
    api.get("/config")
      .then((r) => active && setUrl((r.data.booking_urls || {})[pathway.bookingKey] || r.data.booking_url || ""))
      .catch(() => active && setLoaded(true));
    return () => {
      active = false;
    };
  }, [pathway]);

  const embedUrl = url
    ? `${url}${url.includes("?") ? "&" : "?"}hide_gdpr_banner=1&background_color=fdfbf7&text_color=0b192c&primary_color=c59b27&embed_domain=${encodeURIComponent(window.location.hostname)}&embed_type=Inline`
    : "";

  const perks = [
    { icon: Clock, text: review.duration },
    { icon: Video, text: "Virtual or phone appointment" },
    { icon: GraduationCap, text: "Educational conversation" },
    { icon: HeartHandshake, text: "No obligation, ever" },
  ];

  return (
    <div className="bg-cream min-h-[80vh]">
      <SEO title={`Schedule a ${review.title}`} description={review.description} path={`/book/${pathway.bookingKey}`} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 md:py-14">
        <Link to={pathway.route} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-navy transition-colors min-h-[32px]" data-testid="booking-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {pathway.label}
        </Link>
        <div className="mt-5 flex items-center gap-3">
          <span className={`pw-${pathway.key} pw-chip w-11 h-11 rounded-full flex items-center justify-center shrink-0 ring-2 ring-[#D6BB7C]/70 shadow-md`}>
            <Icon className="w-5 h-5 text-[#F6F1E7]" strokeWidth={2.2} />
          </span>
          <p className="text-xs font-bold uppercase tracking-[0.32em] text-gold">Complimentary Review</p>
        </div>
        <h1 className="mt-4 font-serif text-[34px] sm:text-[40px] lg:text-5xl font-semibold text-navy leading-[1.15] tracking-tight" data-testid="booking-title">{review.title}</h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="booking-description">{review.description}</p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-3">
          {perks.map(({ icon: PIcon, text }) => (
            <li key={text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-sand">
              <PIcon className="w-5 h-5 text-gold shrink-0" />
              <span className="text-sm font-semibold text-navy">{text}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 rounded-3xl border border-sand bg-white overflow-hidden shadow-sm" data-testid="booking-embed">
          {embedUrl ? (
            <>
              {!loaded && (
                <div className="py-16 flex flex-col items-center gap-3 text-navy">
                  <div className="w-9 h-9 rounded-full border-4 border-sand border-t-gold animate-spin" />
                  <p className="text-sm font-semibold">Loading available times…</p>
                </div>
              )}
              <iframe
                title={`Schedule your ${review.title}`}
                src={embedUrl}
                onLoad={() => setLoaded(true)}
                className={`w-full border-0 ${loaded ? "h-[760px]" : "h-0"}`}
                data-testid="booking-iframe"
              />
            </>
          ) : (
            <div className="p-8 text-center text-slate-600">
              <p className="font-semibold text-navy">Our scheduling calendar isn't available right now.</p>
              <p className="mt-1 text-sm">Please email <a className="text-gold font-semibold" href="mailto:legacy@legacywaveconsulting.com">legacy@legacywaveconsulting.com</a> or call (770) 868-6372 and we'll find a time together.</p>
            </div>
          )}
        </div>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid="booking-open-new-tab" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-gold transition-colors">
            <ExternalLink className="w-4 h-4" /> Having trouble? Open the calendar in a new tab
          </a>
        )}
        {pathway.key === "medicare" && (
          <p className="mt-6 text-xs text-slate-500 leading-relaxed" data-testid="booking-medicare-disclaimer">
            Legacy Wave Consulting is not connected with or endorsed by the U.S. government or the federal Medicare program.
            Scheduling a review is your choice and does not enroll you in any plan.
          </p>
        )}
        <p className="mt-4 text-xs text-slate-500 leading-relaxed">
          The purpose of this appointment is to understand your situation before discussing possible options. No coverage, savings, approval, qualification, or outcome is promised.
        </p>
      </div>
    </div>
  );
}
